import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/system";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";

import { useSendTestMessageMutation } from "@/pages/Private/redux/schedule/schedule.api";
import {
	SendTestMessageChannel,
	SendTestMessageRequest,
	SendTestMessageRequestSchema,
	SendTestMessageResponse,
} from "@/pages/Private/pages/Account/schema/sendTestMessage";

import { ERROR_TYPE } from "@/utils";
import { useSnackBar } from "@/utils/useSnackbar";

import { User } from "@/pages/Public/pages/Login/schema/login";

import { TestSendErrors } from "./TestSendErrors";
import { Sender } from "./Sender";
import { TestMessagePreview } from "./TestMessagePreview";

interface SendTestMessageModalProps {
	isOpen: boolean;
	onClose: () => void;
	channel: SendTestMessageChannel;
	subject?: string;
	message: string;
	premiumMessage?: string;
	nodeType?: string;
	inviteVariant?: "standard" | "premium";
	listPersonId?: number;
	campaignSenders?: Sender[];
	sendingUsers?: User[];
}

type EmailSenderOption = AutoCompleteItem & {
	userId: number;
	emailId: number;
};

type LinkedinSenderOption = AutoCompleteItem & {
	userId: number;
	hasLinkedinPremium: boolean;
};

export const SendTestMessageModal: FunctionComponent<SendTestMessageModalProps> = ({
	isOpen,
	onClose,
	channel,
	subject,
	message,
	premiumMessage,
	nodeType,
	inviteVariant,
	listPersonId,
	campaignSenders,
	sendingUsers,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`campaign.sendTest.${key}`);

	const [sendTestMessage, { isLoading, error, reset: resetMutation }] =
		useSendTestMessageMutation();
	const [preview, setPreview] = useState<SendTestMessageResponse | null>(null);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const emailSenderOptions = useMemo<EmailSenderOption[]>(() => {
		return (campaignSenders ?? [])
			.filter(
				(s): s is Sender & { emailId: number; emailAddress: string } =>
					s.active && s.type === "email" && !s.deactivated && !!s.emailId && !!s.emailAddress
			)
			.map((s) => ({
				id: `${s.userId}-${s.emailId}`,
				title: `${s.name} (${s.emailAddress})`,
				userId: s.userId,
				emailId: s.emailId,
			}));
	}, [campaignSenders]);

	const premiumByUserId = useMemo<Record<number, boolean>>(() => {
		const map: Record<number, boolean> = {};

		(sendingUsers ?? []).forEach((u) => {
			if (typeof u.id === "number") {
				map[u.id] = !!u.hasLinkedinPremium;
			}
		});

		return map;
	}, [sendingUsers]);

	const linkedinSenderOptions = useMemo<LinkedinSenderOption[]>(() => {
		return (campaignSenders ?? [])
			.filter((s) => s.active && s.type === "linkedin" && !s.deactivated)
			.map((s) => ({
				id: s.userId,
				title: s.name,
				userId: s.userId,
				hasLinkedinPremium: !!premiumByUserId[s.userId],
			}));
	}, [campaignSenders, premiumByUserId]);

	const isInviteNode = nodeType === "linkedinInviteNode";

	const resolvedInviteMessage = useMemo(() => {
		if (!isInviteNode) {
			return message;
		}

		return inviteVariant === "premium" ? (premiumMessage ?? "") : message;
	}, [isInviteNode, inviteVariant, message, premiumMessage]);

	const defaultValues = useMemo<Partial<SendTestMessageRequest>>(() => {
		if (channel === "email") {
			const first = emailSenderOptions[0];

			return {
				channel: "email",
				listPersonId,
				message,
				subject: subject ?? "",
				senderUserId: first?.userId,
				senderEmailId: first?.emailId,
				recipientEmail: "",
			} as Partial<SendTestMessageRequest>;
		}

		const firstLi = linkedinSenderOptions[0];

		return {
			channel: "linkedin",
			listPersonId,
			message: resolvedInviteMessage,
			senderUserId: firstLi?.userId,
			recipientLinkedinUrl: "",
		} as Partial<SendTestMessageRequest>;
	}, [
		channel,
		emailSenderOptions,
		linkedinSenderOptions,
		listPersonId,
		message,
		resolvedInviteMessage,
		subject,
	]);

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm<SendTestMessageRequest>({
		resolver: zodResolver(SendTestMessageRequestSchema),
		defaultValues: defaultValues as SendTestMessageRequest,
	});

	const formErrorsByName = errors as Record<string, { message?: string } | undefined>;

	const defaultValuesRef = useRef(defaultValues);

	defaultValuesRef.current = defaultValues;

	useEffect(() => {
		if (isOpen) {
			reset(defaultValuesRef.current as SendTestMessageRequest);
			setPreview(null);
			resetMutation();
		}
	}, [isOpen]);

	const recipientEmail = watch("channel" as const) === "email" ? watch("recipientEmail") : "";
	const recipientLinkedinUrl =
		watch("channel" as const) === "linkedin" ? watch("recipientLinkedinUrl") : "";
	const senderUserId = watch("senderUserId");
	const senderEmailId = channel === "email" ? watch("senderEmailId") : undefined;

	const selectedEmailSender = useMemo(() => {
		return emailSenderOptions.find((o) => o.userId === senderUserId && o.emailId === senderEmailId);
	}, [emailSenderOptions, senderUserId, senderEmailId]);

	const selectedLinkedinSender = useMemo(() => {
		return linkedinSenderOptions.find((o) => o.userId === senderUserId);
	}, [linkedinSenderOptions, senderUserId]);

	useEffect(() => {
		if (channel === "linkedin" && isInviteNode) {
			setValue("message", resolvedInviteMessage, { shouldValidate: true });
		}
	}, [resolvedInviteMessage, channel, isInviteNode]);

	const onSubmit = async (values: SendTestMessageRequest) => {
		try {
			const result = await sendTestMessage(values).unwrap();

			setPreview(result);
			handleOpenSnackbar(t("campaign.sendTest.successToast", { sentTo: result.sentTo }) as string);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors)
		.map((e) => (e as { message?: string })?.message)
		.filter(Boolean) as ERROR_TYPE[];

	const inviteVariantMissing =
		channel === "linkedin" && isInviteNode && resolvedInviteMessage.trim().length === 0;

	const senderVariantMismatch =
		channel === "linkedin" &&
		isInviteNode &&
		inviteVariant === "premium" &&
		!!selectedLinkedinSender &&
		!selectedLinkedinSender.hasLinkedinPremium;

	const canSubmit =
		!!listPersonId &&
		!!senderUserId &&
		(channel !== "email" || !!senderEmailId) &&
		!inviteVariantMissing;

	return (
		<Modal
			disableSave={!canSubmit}
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			size="sm"
			submitButtonText={ts("sendButton")}
			title={ts("title")}
		>
			{renderMessageSnackbar()}
			<Stack spacing={2}>
				{channel === "email" ? (
					<Dropdown
						data={emailSenderOptions}
						defaultValue={selectedEmailSender}
						floating={true}
						handleSelect={(value) => {
							const option = emailSenderOptions.find((o) => o.id === value.id);

							if (!option) {
								return;
							}

							setValue("senderUserId", option.userId, { shouldValidate: true });
							setValue("senderEmailId", option.emailId, { shouldValidate: true });
						}}
						label={ts("sender")}
					/>
				) : (
					<div className="flex flex-col gap-1">
						<Dropdown
							data={linkedinSenderOptions}
							defaultValue={selectedLinkedinSender}
							floating={true}
							handleSelect={(value) => {
								const option = linkedinSenderOptions.find((o) => o.id === value.id);

								if (!option) {
									return;
								}

								setValue("senderUserId", option.userId, { shouldValidate: true });
							}}
							label={ts("sender")}
						/>
						{isInviteNode && (
							<div className="text-text-meta text-xs" data-testid="invite-variant-hint">
								{inviteVariant === "premium"
									? ts("inviteVariantPremium")
									: ts("inviteVariantStandard")}
							</div>
						)}
						{senderVariantMismatch && (
							<div className="text-status-warning text-xs" data-testid="invite-variant-mismatch">
								{ts("inviteVariantSenderMismatch")}
							</div>
						)}
						{inviteVariantMissing && (
							<div className="text-status-warning text-xs" data-testid="invite-variant-missing">
								{
									t("campaign.sendTest.inviteVariantMissing", {
										variant:
											inviteVariant === "premium"
												? ts("inviteVariantPremiumShort")
												: ts("inviteVariantStandardShort"),
									}) as string
								}
							</div>
						)}
					</div>
				)}

				{channel === "email" ? (
					<InputField
						error={!!formErrorsByName.recipientEmail?.message}
						handleChange={(e) =>
							setValue("recipientEmail", e.target.value, { shouldValidate: true })
						}
						label={ts("recipientEmail")}
						name="recipientEmail"
						placeholder={ts("recipientEmailPlaceholder")}
						type="email"
						value={recipientEmail ?? ""}
					/>
				) : (
					<InputField
						error={!!formErrorsByName.recipientLinkedinUrl?.message}
						handleChange={(e) =>
							setValue("recipientLinkedinUrl", e.target.value, { shouldValidate: true })
						}
						label={ts("recipientLinkedinUrl")}
						name="recipientLinkedinUrl"
						placeholder={ts("recipientLinkedinUrlPlaceholder")}
						value={recipientLinkedinUrl ?? ""}
					/>
				)}

				{preview && <TestMessagePreview preview={preview} />}

				<TestSendErrors error={error} formErrors={formErrors} />
			</Stack>
		</Modal>
	);
};
